import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { withStyles, withTheme } from '@glu/theming';
import { GrowY } from '@glu/transitions-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import { AccessibilityText } from '@glu/utilities-react';
import {
  XIcon,
  CaretRightIcon,
  CaretDownIcon,
  ExclamationCircleIcon,
  CheckmarkCircleIcon,
  ExclamationTriangleIcon,
  InfoCircleIcon,
  InfoCircleNextIcon,
  CheckmarkCircleNextIcon,
  ExclamationCircleNextIcon
} from '@glu/icons-react';
import locale from '@glu/locale';
import styles from './Alert.styles';
import './themeDefaults';

export function AlertIcon({
  // eslint-disable-next-line react/prop-types
  variant, suppressIcon, className, theme
}) {
  const iconMap = {
    error: ExclamationTriangleIcon,
    info: theme && theme.name === 'legacy' ? InfoCircleIcon : InfoCircleNextIcon,
    success: theme && theme.name === 'legacy' ? CheckmarkCircleIcon : CheckmarkCircleNextIcon,
    warning: theme && theme.name === 'legacy' ? ExclamationCircleIcon : ExclamationCircleNextIcon
  };
  const Icon = iconMap[variant];

  if (suppressIcon || !Icon) {
    return null;
  }

  return <Icon viewBox="0 0 16 16" title={locale.get(variant)} className={className} />;
}

AlertIcon.propTypes = {
  theme: PropTypes.shape({ name: PropTypes.string }).isRequired,
  className: PropTypes.string.isRequired,
  suppressIcon: PropTypes.bool.isRequired,
  variant: PropTypes.string.isRequired
};

class Alert extends Component {
  alertRef = React.createRef();

  // eslint-disable-next-line react/static-property-placement
  static propTypes = {

    /**
     * Message in dropdown, between <Alert></Alert>
     */
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.element, PropTypes.node, PropTypes.func])),
      PropTypes.element,
      PropTypes.node,
      PropTypes.func
    ]),

    /**
     * Internal styles
     */
    classes: PropTypes.objectOf(PropTypes.string).isRequired,

    /**
     * Hide and show capability for alert message
     */
    details: PropTypes.bool,

    /**
     * Dismiss the alert after 'n' milliseconds by calling 'onHide' function
     */
    duration: PropTypes.number,

    /**
     * Automatically focus
     */
    autoFocus: PropTypes.bool,

    /**
     * Add a close button to the alert
     */
    dismissible: PropTypes.bool,
    mountOnEnter: PropTypes.bool,

    /**
     * Callback when the alert is dismissed
     */
    onHide: PropTypes.func,

    /**
     * Don't show alert if show is false
     */
    show: PropTypes.bool,

    /**
     * Hide icon next to title
     */
    suppressIcon: PropTypes.bool,

    /**
     * Main alert message
     */
    title: PropTypes.node,

    /**
     * Additional message
     */
    message: PropTypes.node,
    unmountOnExit: PropTypes.bool,

    /**
     * Type of alert
     */
    variant: PropTypes.oneOf(['error', 'info', 'primary', 'secondary', 'success', 'warning']),

    /**
     * Custom class on root level
     */
    className: PropTypes.string,

    /**
     * data-qa attribute
     */
    dataQa: PropTypes.string,

    /**
     * Actual theme in use e.g. ash, legacy
     */
    theme: PropTypes.shape({})
  };

  // eslint-disable-next-line react/static-property-placement
  static defaultProps = {
    children: [],
    details: false,
    autoFocus: false,
    dismissible: true,
    duration: 0,
    mountOnEnter: true,
    show: true,
    onHide() {},
    suppressIcon: false,
    title: '',
    message: '',
    unmountOnExit: true,
    variant: 'info',
    className: '',
    dataQa: '',
    theme: {}
  };

  // eslint-disable-next-line react/state-in-constructor
  state = {
    displayDetails: !this.props.details,
    show: this.props.show
  };

  componentDidMount() {
    const { autoFocus, duration } = this.props;

    if (autoFocus && this.alertRef.current) {
      this.alertRef.current.focus();
    }
    if (duration) {
      this.hideTimeout = setTimeout(this.close, duration);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.hideTimeout);
  }

  close = (event) => {
    this.setState({ show: false });
    this.props.onHide(event);
  };

  toggleDisplay = () => {
    this.setState((state) => ({ displayDetails: !state.displayDetails }));
  };

  render() {
    const {
      children,
      classes,
      details,
      dismissible,
      duration,
      show,
      mountOnEnter,
      onHide,
      suppressIcon,
      title,
      message,
      unmountOnExit,
      variant,
      className,
      dataQa,
      theme,
      ...rest
    } = this.props;

    // Don't show alert if show is false
    if (!this.state.show) {
      return null;
    }

    return (
      <div
        data-qa={dataQa || classes.root}
        ref={this.alertRef}
        role="alert"
        tabIndex="-1"
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        className={classnames(classes.root, className, classes[variant])}
      >
        <AlertIcon
          className={classes.alertIcon}
          suppressIcon={suppressIcon}
          variant={variant}
          theme={theme}
        />

        {details ? (
          <Button
            variant={TERTIARY}
            onClick={this.toggleDisplay}
            className={classes.btnDetails}
          >
            <span className={classes.labelDetails}>{locale.get('details')}</span>
            {this.state.displayDetails ? (
              <CaretDownIcon className={classes.IconDetails} title={locale.get('details')} />
            ) : (
              <CaretRightIcon className={classes.IconDetails} title={locale.get('details')} />
            )}
          </Button>
        ) : null }

        <div className={classes.alertDetails}>
          {title ? (
            <p
              className={classnames(classes.alertTitle, { [classes.titleWithMessage]: !!message })}
            >
              {title}
            </p>
          ) : null }

          {message ? (<p className={classes.alertMessage}>{message}</p>) : null}
          {React.Children.count(children) > 0 && (
            <GrowY
              in={this.state.displayDetails}
              mountOnEnter={mountOnEnter}
              unmountOnExit={unmountOnExit}
            >
              <div className={classes.alertDetailsContent}>
                { children }
              </div>
            </GrowY>
          )}
        </div>

        {dismissible ? (
          <Button className={classes.close} variant={TERTIARY} onClick={this.close}>
            <AccessibilityText>{locale.get('alert.close')}</AccessibilityText>
            <XIcon noIconWrapper title={locale.get('alert.close')} />
          </Button>
        ) : null }
      </div>
    );
  }
}

export const AlertBase = Alert;
const ThemedAlert = withTheme(withStyles(styles)(Alert));
ThemedAlert.displayName = 'Alert';
export default ThemedAlert;
